import React, { useEffect, useState } from "react";
import { Quote, X } from "lucide-react";
import { api } from "../../lib/api";
import { trackEvent } from "../../lib/track";

const DISMISSED_KEY = "am-reflection-trigger-dismissed-v1";
const ARM_DELAY_MS = 60_000;          // 60s minimum dwell time
const ARM_SCROLL_DEPTH = 0.55;        // OR 55% of page depth
const HOMEPAGE_ONLY_PATHS = new Set([
  "/", "/about", "/journal", "/book", "/start-here", "/voices",
]);

/**
 * FloatingReflectionTrigger.
 *
 * Subtle floating cue that appears after either 60 seconds of dwell
 * time OR ~55% scroll depth, whichever fires first. Designed to land
 * AFTER the visitor has read something meaningful, not while the hero
 * is still loading. Dismissal persists across the session in
 * localStorage so it never re-appears the same day.
 *
 * Visual: a pill-shaped black/gold mark at bottom-right. Label cycles
 * between "Share a reflection" and "What resonated?" on each mount so
 * the surface stays alive across return visits.
 *
 * When clicked, opens a calm modal posting a NEW reflection to the
 * /api/testimonials endpoint with `pending` status (existing admin
 * moderation queue catches it).
 */
const FloatingReflectionTrigger = () => {
  const [armed, setArmed] = useState(false);
  const [open, setOpen] = useState(false);

  // Pick label deterministically based on minute-of-day so the cue
  // feels different across sessions without flickering on re-render.
  const label = (new Date().getMinutes() % 2 === 0)
    ? "Share a reflection"
    : "What resonated?";

  useEffect(() => {
    // Only on selected long-form public surfaces.
    if (typeof window === "undefined") return undefined;
    if (!HOMEPAGE_ONLY_PATHS.has(window.location.pathname)) return undefined;
    // Respect prior dismissal.
    try {
      if (localStorage.getItem(DISMISSED_KEY)) return undefined;
    } catch {
      /* private mode */
    }

    let dwellTimer = null;
    let unmounted = false;

    const arm = () => {
      if (unmounted || armed) return;
      setArmed(true);
      trackEvent("reflection_trigger_armed");
      window.removeEventListener("scroll", onScroll);
      if (dwellTimer) clearTimeout(dwellTimer);
    };

    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const docH = document.documentElement.scrollHeight || 1;
      if (scrolled / docH > ARM_SCROLL_DEPTH) arm();
    };

    dwellTimer = setTimeout(arm, ARM_DELAY_MS);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      unmounted = true;
      if (dwellTimer) clearTimeout(dwellTimer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [armed]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, "1"); } catch { /* private */ }
    setArmed(false);
    trackEvent("reflection_trigger_dismissed");
  };

  const handleOpen = () => {
    setOpen(true);
    trackEvent("reflection_trigger_opened", { label });
  };

  if (!armed && !open) return null;

  return (
    <>
      {armed ? (
        <div
          data-testid="reflection-floating-trigger"
          className="reflection-floating-trigger"
          role="region"
          aria-label="Share a reflection"
        >
          <button
            type="button"
            data-testid="reflection-floating-trigger-open"
            onClick={handleOpen}
            className="reflection-floating-trigger-btn group"
          >
            <Quote size={12} strokeWidth={1.6} className="text-gold/85 group-hover:text-gold transition-colors" aria-hidden />
            <span>{label}</span>
          </button>
          <button
            type="button"
            data-testid="reflection-floating-trigger-dismiss"
            aria-label="Dismiss"
            onClick={dismiss}
            className="reflection-floating-trigger-dismiss"
          >
            <X size={11} strokeWidth={1.6} aria-hidden />
          </button>
        </div>
      ) : null}
      {open ? (
        <ReflectionModal onClose={() => setOpen(false)} entryLabel={label} />
      ) : null}
    </>
  );
};

/**
 * ReflectionModal.
 *
 * Lighter, calmer variant of the full testimonial submission form.
 * Three fields, one toggle, one CTA. Posts to /api/testimonials with
 * status="pending" so admin moderation gates publication.
 */
const ReflectionModal = ({ onClose, entryLabel }) => {
  const [name, setName] = useState("");
  const [reflection, setReflection] = useState("");
  const [email, setEmail] = useState("");
  const [mayShare, setMayShare] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!reflection.trim() || reflection.trim().length < 20) {
      setError("Share at least a sentence or two.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/testimonials", {
        name: name.trim() || "Anonymous",
        role: "Reflection",
        testimonial: reflection.trim(),
        email: email.trim() || undefined,
        ai_consent: false,
        share_publicly: mayShare,
      });
      trackEvent("reflection_submitted", {
        share_publicly: mayShare,
        has_name: Boolean(name.trim()),
        has_email: Boolean(email.trim()),
      });
      setDone(true);
    } catch (err) {
      setError("Could not send. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="reflection-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reflection-modal-title"
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 backdrop-blur-md px-4 sm:px-6 py-10"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-[#0a0a0a] border border-gold/25 rounded-lg shadow-[0_0_80px_-30px_rgba(212,175,55,0.5)] p-7 sm:p-9"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="reflection-modal-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-themed-muted/70 hover:text-gold transition-colors"
        >
          <X size={16} aria-hidden />
        </button>

        {done ? (
          <div data-testid="reflection-modal-thanks" className="text-center py-4">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">Received</p>
            <h2 className="font-serif text-2xl text-themed font-light leading-snug">
              Thank you for{" "}
              <span className="italic gold-gradient-text">trusting us</span>{" "}
              with your words.
            </h2>
            <p className="mt-4 text-white/85 text-[14.5px] leading-relaxed max-w-md mx-auto">
              We review every reflection by hand. If you said yes to sharing, we will reach back before anything lands on the site.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="reflection-modal-done"
              className="btn-ghost-gold mt-7 inline-flex"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-3">
              {entryLabel}
            </p>
            <h2
              id="reflection-modal-title"
              className="font-serif text-2xl text-themed font-light leading-snug"
            >
              A few words is enough.
            </h2>
            <p className="mt-3 text-white/85 text-[14px] leading-relaxed">
              Anything that landed. Anything that shifted. Anything you wish more people saw on a page like this.
            </p>

            <div className="mt-7 space-y-5">
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-gold/85">Name <span className="opacity-60">(optional)</span></span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="reflection-modal-name"
                  className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-gold/70 outline-none text-themed text-[15px] py-2 transition-colors"
                  placeholder="How you would like to be credited"
                />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-gold/85">Your reflection</span>
                <textarea
                  required
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  data-testid="reflection-modal-text"
                  rows={4}
                  className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-gold/70 outline-none text-themed text-[15px] py-2 leading-relaxed resize-none transition-colors"
                  placeholder="What landed. What shifted."
                />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.3em] uppercase text-gold/85">Email <span className="opacity-60">(optional)</span></span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="reflection-modal-email"
                  className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-gold/70 outline-none text-themed text-[15px] py-2 transition-colors"
                  placeholder="If you want a quiet reply"
                />
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mayShare}
                  onChange={(e) => setMayShare(e.target.checked)}
                  data-testid="reflection-modal-share"
                  className="mt-1 accent-gold"
                />
                <span className="text-[13px] text-white/85 leading-relaxed">
                  May we share this reflection on the site? You can stay anonymous if you prefer.
                </span>
              </label>
            </div>

            {error ? (
              <p className="mt-4 text-[12px] text-rose-300/90" data-testid="reflection-modal-error">
                {error}
              </p>
            ) : null}

            <div className="mt-7 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <button
                type="submit"
                disabled={busy}
                data-testid="reflection-modal-submit"
                className="btn-gold inline-flex w-full sm:w-auto items-center justify-center disabled:opacity-60"
              >
                {busy ? "Sending..." : "Send Reflection"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-[11px] tracking-[0.22em] uppercase text-themed-muted/70 hover:text-gold transition-colors"
              >
                Not now
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FloatingReflectionTrigger;
